import { Directory, Filesystem } from '@capacitor/filesystem';
import { ST } from 'types';
import { RunModel, RunModelModel } from '../../../models/RunModel/RunModel';
import { UserSN } from '../../../models/User';
import {
    ExploreElementIn,
    ExploreElementOut,
    ExploreElementCreateIn,
} from '../../../models/ExploreElementModel/ExploreElementModel';
import { Area, ICloudStorage } from '../CloudStorageService';
import { logger } from 'services/Logger';

const DUMMY_USER: UserSN = {
    // _AuthUserModel
    uid: 'Dummy-UID',
    displayName: 'DummyUser',
    email: 'Dummy@User.de',
    photoURL: 'https://spinnax.com/wp-content/uploads/2022/01/logo-240x143.png',

    // _UserModel
    id: 'DummyID-123',
    realName: 'Dummy User',
    favoriteRuns: [],
    friendRuns: [],
    accountType: null,
    address: null,
    account: null,
    birthday: null,
    board: null,
} as unknown as UserSN;

// const TEST_DEVICE_ID = 'F8_FE_46_D6_2E_54';
const TEST_DEVICE_ID = 'DB_29_AD_D8_7F_E2';
// const TEST_RECORD_IDS = [
//   '2023_03_03_16_07_39',
//   '2023_03_03_16_13_56',
//   '2023_03_03_16_27_46',
// ];
const TEST_RECORD_IDS = ['2023_04_27_17_42_02'];

const plays: Record<string, ST.Play> = {};
TEST_RECORD_IDS.sort().forEach((id, idx) => {
    const data: ST.Play = require(`./TestData/Play/Play_${TEST_DEVICE_ID}_${id}.json`);
    data.title = `DUMMY_DATA_${idx}`;
    plays[`${TEST_DEVICE_ID}_${id}`] = data;
});
const videos: Record<string, string> = {};
const getVideoUri = async (id: string) => {
    try {
        const uri = (
            await Filesystem.getUri({
                directory: Directory.Data,
                path: `Video_${id}.mp4`,
            })
        ).uri;
        videos[id] = uri;
        return uri;
    } catch (e: any) {
        return null;
    }
};

let refDate = new Date().getTime();
const trickRecords: Array<RunModel> = [];
TEST_RECORD_IDS.sort().forEach(async (id) => {
    const recordId = `${TEST_DEVICE_ID}_${id}`;
    let videoUrl: string | null = await getVideoUri(recordId);
    let localVideoFileName = `Video_${recordId}.mp4`;
    refDate -= 1000;
    const date = new Date(refDate);
    trickRecords.push(
        RunModelModel.create({
            id: recordId,
            deviceId: TEST_DEVICE_ID,
            recordId: id,
            date, // always appear on top
            shareUrl: recordId,
            skater: 'John Doe',
            playUrl: `${recordId}`,
            localVideoFileName,
            videoUrl,
        })
    );
});

class DummyCloudStorage implements ICloudStorage {
    tricks: any[] = [];
    exploreElements: any[] = [];

    destroy() { }
    async addRun(trick: RunModel): Promise<void> {
        const userId = 'DummyUser';
        if (trick.shareUrl) {
            logger.info(`Run seems to have been already uploaded`);
        } else {
            // The fields in trick are immutable so we need to
            // deep copy the object an mutate on the copy
            const x = trick.toServer(userId);

            x.shareUrl = `sharable${trick.id}`;

            this.tricks.push(x);
        }

        return new Promise((resolve) => {
            setTimeout(() => {
                resolve();
            }, 1000);
        });
    }

    async updateRun(trick: RunModel, _area?: Area): Promise<void> {
        // Find and update the trick in the dummy storage
        const existingRunIndex = this.tricks.findIndex(
            (t) => t.shareUrl === trick.shareUrl || t.id === trick.id
        );

        if (existingRunIndex !== -1) {
            const userId = 'DummyUser';
            const updatedRun = trick.toServer(userId);
            updatedRun.shareUrl = trick.shareUrl || `sharable${trick.id}`;
            this.tricks[existingRunIndex] = updatedRun;
            logger.info(`Dummy: Updated trick ${trick.id}`);
        } else {
            logger.warn(`Dummy: Run ${trick.id} not found for update`);
        }

        return new Promise((resolve) => {
            setTimeout(() => {
                resolve();
            }, 500);
        });
    }

    async getRunsByUser(_uid: string): Promise<Array<RunModel>> {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve(trickRecords);
            }, 1000);
        });
    }

    async getNewerPublicRuns(refDate?: Date): Promise<Array<RunModel>> {
        return Promise.reject("Not implemented")
    }
    async getOlderPublicRuns(refDate?: Date): Promise<Array<RunModel>> {
        return Promise.reject("Not implemented")
    }
    async getPublicRuns(): Promise<Array<RunModel>> {
        return new Promise((resolve) => {
            setTimeout(() => {
                // Filter only public tricks from dummy data
                const publicRuns = trickRecords.filter((trick) => trick.isPublic);
                resolve(publicRuns);
            }, 1000);
        });
    }
    async getFriendsRuns(): Promise<Array<RunModel>> {
        return new Promise((resolve) => {
            setTimeout(() => {
                // For dummy implementation, return the same public tricks as friends' tricks
                const friendsRuns = trickRecords.filter((trick) => trick.isPublic);
                resolve(friendsRuns);
            }, 1000);
        });
    }
    async getRunsById(_ids: Array<string>): Promise<Array<RunModel>> {
        return [];
    }
    async getRun(_id: string): Promise<RunModel | null> {
        return null;
    }
    async delRun(_id: string): Promise<void> {
        return;
    }
    async getUser(_uid: string): Promise<UserSN | null> {
        return DUMMY_USER;
    }
    async getAllUsers(): Promise<Array<UserSN>> {
        return [DUMMY_USER];
    }
    async putUser(_user: UserSN): Promise<void> {
        return;
    }

    async downloadVideo(_url: string) {
        return _url;
    }
    async uploadVideo(
        _id_device: string,
        _id_record: string,
        _inBackground: boolean,
        _area?: Area
    ) {
        await new Promise((resolve) => {
            setTimeout(() => {
                resolve(null);
            }, 1000);
        });
        return 'uploaded';
    }

    async downloadPlay(
        device_id: string,
        record_id?: string,
        _area?: Area
    ): Promise<{ data: ST.Play; uri: string }> {
        const recordId = record_id ? `${device_id}_${record_id}` : device_id;
        const data = plays[recordId];
        if (data) {
            return new Promise((res) => {
                setTimeout(() => {
                    res({ data, uri: 'some uri' });
                }, 3000);
            });
        }
        throw new Error(`Play ${device_id}|${record_id} not found!`);
    }
    async uploadRecord(_record: ST.Record, _area?: Area) {
        return '';
    }
    async uploadPlay(_play: ST.Play, _area?: Area) {
        logger.debug('Mock.uploadPlay', _play);
        return '';
    }

    // ExploreElement methods
    async addExploreElement(element: ExploreElementCreateIn): Promise<string> {
        const newId = `dummy-${Date.now()}`;
        const newElement = {
            ...element,
            id: newId,
            uid: element.uid || 'dummy-user',
            country: element.country || '',
            gender: element.gender || '',
            age: element.age || 0,
            location: element.location || '',
            nationality: element.nationality || '',
        };
        this.exploreElements.push(newElement);
        logger.debug('Dummy: ExploreElement added', newElement.id);
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve(newId);
            }, 500);
        });
    }

    async getExploreElements(_area?: Area): Promise<Array<ExploreElementOut>> {
        return new Promise((resolve) => {
            setTimeout(() => {
                // Remove uid before returning as ExploreElementOut
                const elements = this.exploreElements.map(
                    ({ uid: _uid, ...rest }) => rest
                );
                resolve(elements as ExploreElementOut[]);
            }, 500);
        });
    }

    async getExploreElementsByUser(
        uid: string,
        _area?: Area
    ): Promise<Array<ExploreElementOut>> {
        return new Promise((resolve) => {
            setTimeout(() => {
                const userElements = this.exploreElements
                    .filter((el) => el.uid === uid)
                    .map(({ uid: _uid, ...rest }) => rest);
                resolve(userElements as ExploreElementOut[]);
            }, 500);
        });
    }

    async getExploreElement(
        id: string,
        _area?: Area
    ): Promise<ExploreElementOut | null> {
        return new Promise((resolve) => {
            setTimeout(() => {
                const element = this.exploreElements.find((el) => el.id === id);
                if (element) {
                    const { uid: _uid, ...rest } = element;
                    resolve(rest as ExploreElementOut);
                } else {
                    resolve(null);
                }
            }, 500);
        });
    }

    async updateExploreElement(element: ExploreElementIn): Promise<void> {
        const index = this.exploreElements.findIndex((el) => el.id === element.id);
        if (index !== -1) {
            this.exploreElements[index] = {
                ...element,
                uid: this.exploreElements[index].uid,
                country: element.country || '',
                gender: element.gender || '',
                age: element.age || 0,
                location: element.location || '',
                nationality: element.nationality || '',
            };
        }
        logger.debug('Dummy: ExploreElement updated', element.id);
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve();
            }, 500);
        });
    }

    async deleteExploreElement(id: string, _area?: Area): Promise<void> {
        const index = this.exploreElements.findIndex((el) => el.id === id);
        if (index !== -1) {
            this.exploreElements.splice(index, 1);
        }
        logger.debug('Dummy: ExploreElement deleted', id);
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve();
            }, 500);
        });
    }

    async uploadProfilePhoto(userId: string, file: File, area?: Area): Promise<string> {
        logger.info(`DummyCloudStorage.uploadProfilePhoto: userId=${userId}, file=${file.name}, area=${area}`);
        // Simuliere Upload und gib eine Dummy-URL zurück
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve(`https://dummy.local/${area || 'default'}/Images/${userId}/${file.name}`);
            }, 500);
        });
    }
}

export default DummyCloudStorage;
